import { AsyncSubject, BehaviorSubject, EMPTY, merge, Observable } from 'rxjs';
import {
  map,
  distinctUntilChanged,
  share,
  filter,
  switchMap,
  takeUntil,
  endWith,
} from 'rxjs/operators';
import {
  PositionedModelController,
  VisualElementController,
  DataVisualElementController,
  ExplainTextInfo,
  DataItem,
  BIRDModelController,
  PromptElementController,
} from '@sas-dvr/ltjs-bird';
import { AnyAction } from 'redux';
import { filterNulls } from './arrayUtils';
import { StateObservable } from 'redux-observable';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import {
  ReportContextMenuItem,
  ReportContextMenuState,
} from '@sas-dvr/internal-va-react-core/store/reportContextMenu/ReportContextMenuState';
import { TransportVoucher } from '../../transport/TransportVoucher';
import { ExplanationState } from '@sas-dvr/internal-va-react-core/store/explanation/explanationState';
import { selectLayoutState } from '@sas-dvr/internal-va-react-core/store/selectors';
import { ContextMenuItems } from './contextMenu';

export function getExplainObservables(
  id: string,
  visualController: PositionedModelController | null,
  visualElementController: VisualElementController,
  state$: StateObservable<ReportState>,
  unload$: Observable<BIRDModelController>
) {
  if (
    !(visualElementController instanceof DataVisualElementController) ||
    // Ignore prompts
    visualElementController instanceof PromptElementController ||
    // Ignore supplemental visuals
    !visualController
  ) {
    return EMPTY;
  }

  const isContextMenuTarget$ = state$.pipe(
    map((state) => {
      const targetId = state.ui.reportContextMenu.target?.id;
      return (
        !!targetId &&
        (targetId === id || selectLayoutState(state$.value, targetId).compositeParentKey === id)
      );
    }),
    distinctUntilChanged(),
    share()
  );

  const dataItems$ = new BehaviorSubject<DataItem[] | undefined>(undefined);

  // Get and retain DataItems when the visual is the context menu target
  isContextMenuTarget$.pipe(takeUntil(unload$), endWith(false)).subscribe((isTarget) => {
    const dataItems = isTarget
      ? filterNulls(visualElementController.getSelectedDataItemsForExplainText())
      : undefined;
    dataItems$.value?.forEach((item) => item.release());
    dataItems$.next(dataItems);
    dataItems$.value?.forEach((item) => item.retain());
  });

  const updateContextMenu$ = isContextMenuTarget$.pipe(
    filter((isTarget) => isTarget),
    map(() => {
      const items: ReportContextMenuItem[] = filterNulls(
        dataItems$.value?.map((item) => {
          const name = item.getName();
          const label = visualElementController.getDataItemInfo(item)?.getLabel();
          if (!name || !label) {
            return null;
          }
          return {
            text: label,
            action: ExplanationState.actions.set({ dataItem: { name, label } }),
          };
        })
      );
      return ReportContextMenuState.actions.setObjectItem(
        ContextMenuItems.EXPLAIN,
        items.length
          ? {
              text: { key: 'ContextMenu.explain.txt' },
              subSections: [{ items }],
            }
          : undefined
      );
    })
  );

  const fetchExplanation$ = state$.pipe(
    map((state) => state.ui.explanation.dataItem),
    distinctUntilChanged(), // Switch (ignore results) if explanation target changes
    switchMap((target) => {
      const dataItems = dataItems$.value;
      if (!target || !dataItems) {
        return EMPTY;
      }
      // AsyncSubject emits latest value on completion
      const result$ = new AsyncSubject<AnyAction>();

      // Reset explanation state if we don't get a displayable resource or warning
      result$.next(ExplanationState.actions.reset());

      // Get explain text from server
      const dataItem = dataItems.find((item) => item.getName() === target.name);
      const rawVoucher = dataItem && visualElementController.getExplainTextForDataItem(dataItem);
      if (rawVoucher) {
        new TransportVoucher(rawVoucher)
          .fulfilled((voucher) => {
            const resource = voucher.getRequestedResource();
            const content = resource instanceof ExplainTextInfo ? resource.getContent() : null;
            if (content) {
              result$.next(ExplanationState.actions.update({ content }));
            }
          })
          .warning((voucher) => {
            const warning = voucher.getWarning()?.getMessage();
            if (warning) {
              result$.next(ExplanationState.actions.update({ warning }));
            }
          })
          .always(() => {
            result$.complete();
          });
      } else {
        result$.complete();
      }
      return result$;
    })
  );

  return merge(updateContextMenu$, fetchExplanation$);
}
